import { Spacing } from '@persona-id/design-tokens';
import { XIcon } from '@persona-id/design-tokens/icons';
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { connect, ConnectedProps, useDispatch } from 'react-redux';
import styled from 'styled-components';

import {
  AspectRatioGuard,
  AssetManagementModal,
  Caption,
  CaptionText,
  convertAssetKeyToDisplayName,
  PreviewImageInner,
  renderPersonaAsset,
  Thumbnail,
} from 'dashboard/apps/InquiryTemplateEditor/AssetManagementModal/AssetManagementModal';
import {
  Asset,
  AssetUploadData,
  UploadedAsset,
} from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateEditor/types';
import { ComponentSettingImage } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/types';
import { SCREEN_EDITOR_CONTROL_PANEL_ID } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenEditor/InquiryTemplateScreenEditor';
import MaximizablePanel from 'dashboard/components-lib/MaximizablePanel';
import { MessageType } from 'dashboard/lib/interfaces/Message';
import { showMessage } from 'dashboard/redux/actions/messages';
import { RootState } from 'dashboard/redux/store';
import { uploadAssets } from 'dashboard/redux/thunk-actions/inquiry-template';
import { INQUIRY_TEMPLATE_EDITOR_SHOW_VIEW_ID } from 'dashboard/views/DashboardInquiryTemplatesShowView/DashboardInquiryTemplatesShowView';
import Button from 'design-system/components/Button';
import { Paragraph } from 'design-system/components/Text';
import useApiCallback from 'lib/hooks/useApiCallback';
import useApiErrorHandler from 'lib/hooks/useApiErrorHandler';
import useToggle from 'lib/hooks/useToggle';
import { ImageLocalComponentConfig, UiImageComponentConfig } from 'lib/interfaces/ui-components';
import { isPermissionsError, permissionsErrorMessage } from 'lib/permissions-errors';

const LOTTIE_FILE_DIMENSIONS_PROPS = {
  height: '100%',
  width: '100%',
};

const RootContainer = styled.div``;

const TopContainer = styled.div`
  display: flex;
  width: 100%;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${Spacing[4]};
`;

const PreviewContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 160px;
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.color.border.secondary};
`;

const PreviewCaption = styled(Caption)`
  background-color: ${({ theme }) => theme.color.background.secondary};
`;

const PreviewCaptionText = styled(CaptionText)`
  color: ${({ theme }) => theme.color.text.primary};
`;

const ButtonContainer = styled.div`
  display: flex;
`;

const RemoveImageButton = styled(Button)`
  position: absolute;
  right: 10px;
  top: 10px;
`;

interface AssetPreviewDisplayProps {
  assetInfo?: UiImageComponentConfig['config'] | string;
  offsetId?: string;
  disabled: boolean;
  personaAssetsDisabled?: boolean;
  remoteAssetsDisabled?: boolean;
  imageSetting?: ComponentSettingImage<UiImageComponentConfig>;
  handleImageSelect: (asset: Asset) => void;
  handleRemoveAsset?: () => void;
}

export const ConnectedAssetPreviewDisplay = ({
  assets,
  assetInfo,
  offsetId = SCREEN_EDITOR_CONTROL_PANEL_ID,
  disabled,
  imageSetting,
  personaAssetsDisabled,
  remoteAssetsDisabled,
  handleImageSelect,
  handleRemoveAsset,
}: AssetPreviewDisplayProps & PropsFromRedux) => {
  const dispatch = useDispatch();
  const [imagePreview, setImagePreview] = useState<React.ReactNode | null>(null);
  const [imageCaption, setImageCaption] = useState<string | null>(null);

  const [
    showAssetManagementPanel,
    { enable: openAssetManagementPanel, disable: closeAssetManagementPanel },
  ] = useToggle(false);

  const {
    callback: handleUploadAssets,
    loading: assetUploadsLoading,
    error: uploadAssetsError,
  } = useApiCallback(async (files: AssetUploadData[]) => {
    await dispatch(uploadAssets(files));
  }, []);

  useApiErrorHandler({
    error: uploadAssetsError,
    onError: (err) => {
      if (isPermissionsError(err)) {
        dispatch(showMessage(permissionsErrorMessage(err), MessageType.Failure));
      } else if (err.status === 422) {
        dispatch(
          showMessage('Only images and valid lottie JSON files are accepted.', MessageType.Failure),
        );
      } else {
        dispatch(showMessage('Failed to upload asset. Please try again.', MessageType.Failure));
      }
    },
    onNetworkError: () =>
      dispatch(
        showMessage('We are experiencing network issues. Please try again.', MessageType.Failure),
      ),
  });

  useEffect(() => {
    if (assetInfo != null) {
      if (typeof assetInfo !== 'string') {
        if (!imageSetting) return;
        /* @ts-expect-error TODO(Ernest): figure out typing for generic attribuet names */
        if (assetInfo[imageSetting['client-key']]) {
          const asset = assets.find(
            /* @ts-expect-error TODO(Ernest): figure out typing for generic attribuet names */
            (asset) => asset['blob-id'] === assetInfo[imageSetting['client-key']],
          );
          if (asset) {
            setImageCaption(asset.filename);
            setImagePreview(
              <Thumbnail
                {...LOTTIE_FILE_DIMENSIONS_PROPS}
                src={asset.url}
                contentType={asset['content-type']}
              />,
            );
          }
        } else if (
          (assetInfo as ImageLocalComponentConfig['config'])[imageSetting['local-image-key']]
        ) {
          const imageKey = (assetInfo as ImageLocalComponentConfig['config'])[
            imageSetting['local-image-key']
          ];
          setImageCaption(convertAssetKeyToDisplayName(imageKey));
          setImagePreview(renderPersonaAsset(imageKey));
        } else {
          setImageCaption(null);
          setImagePreview(null);
        }
      } else {
        // if assetInfo is a string, it should be the blob id
        const asset = assets.find((asset) => assetInfo === asset['blob-id']);
        if (asset) {
          setImageCaption(asset.filename);
          setImagePreview(
            <Thumbnail
              {...LOTTIE_FILE_DIMENSIONS_PROPS}
              src={asset.url}
              contentType={asset['content-type']}
            />,
          );
        }
      }
    } else {
      setImageCaption(null);
      setImagePreview(null);
    }
  }, [assetInfo, assets]);

  const hasAsset = imagePreview != null && imageCaption != null;
  const manageAssetButtonLabel = hasAsset ? 'Replace' : 'Add';

  return (
    <RootContainer>
      <TopContainer>
        <Paragraph color='primary'>Asset</Paragraph>
        <ButtonContainer>
          <Button
            id='asset-preview-display__manage-asset-button'
            size='sm'
            width='auto'
            variant='basic'
            aria-label={manageAssetButtonLabel}
            disabled={disabled}
            onClick={openAssetManagementPanel}
          >
            {manageAssetButtonLabel}
          </Button>
        </ButtonContainer>
      </TopContainer>
      {hasAsset && (
        <>
          <PreviewContainer>
            {handleRemoveAsset != null && (
              <RemoveImageButton
                id='asset-preview-display__remove-asset-button'
                size='sm'
                width='icon'
                variant='basic'
                aria-label='Remove asset'
                disabled={disabled}
                onClick={handleRemoveAsset}
              >
                <XIcon />
              </RemoveImageButton>
            )}
            <PreviewImageInner>
              <AspectRatioGuard>{imagePreview}</AspectRatioGuard>
            </PreviewImageInner>
            <PreviewCaption title={imageCaption}>
              <PreviewCaptionText>{imageCaption}</PreviewCaptionText>
            </PreviewCaption>
          </PreviewContainer>
        </>
      )}
      {showAssetManagementPanel &&
        createPortal(
          <MaximizablePanel
            title='Manage assets'
            offsetElementId={offsetId}
            onOutsideClickAndEscPress={closeAssetManagementPanel}
            disableMaximize
          >
            <AssetManagementModal
              assets={assets}
              isLoading={assetUploadsLoading}
              personaAssetsDisabled={personaAssetsDisabled}
              remoteAssetsDisabled={remoteAssetsDisabled}
              handleClose={closeAssetManagementPanel}
              handleFileUpload={handleUploadAssets}
              handleImageSelect={handleImageSelect}
            />
          </MaximizablePanel>,
          document.getElementById(INQUIRY_TEMPLATE_EDITOR_SHOW_VIEW_ID) as Element,
        )}
    </RootContainer>
  );
};

const mapStateToProps = (state: RootState) => {
  const assets: UploadedAsset[] = state.inquiryTemplate.assets;
  return {
    assets,
  };
};

const connector = connect(mapStateToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(ConnectedAssetPreviewDisplay);
